import { ChangeEvent, FocusEvent, KeyboardEvent, useCallback } from 'react';
import { Columns, Form } from 'react-bulma-components';
import { FormFieldData } from '../../../shared/form';
import FeedbackMessage from '../../../ui/feedback-message/feedback-message';

export type InviteCustomerUserFields = {
  email: string;
};

export type InviteCustomerUserDetailsFormData =
  FormFieldData<InviteCustomerUserFields>;

type InviteCustomerUserFormChangeEvent = ChangeEvent<
  HTMLInputElement | HTMLSelectElement
>;

type InviteCustomerUserFormBlurEvent = FocusEvent<
  HTMLInputElement | HTMLSelectElement,
  Element
>;

export type InviteCustomerUserDetailsFormValidation = Record<
  keyof InviteCustomerUserFields,
  ((value: string) => string | null) | null
>;

export type InviteUsersFormProps = {
  data: InviteCustomerUserDetailsFormData;
  onChangeData: (data: InviteCustomerUserDetailsFormData) => void;
  validate: InviteCustomerUserDetailsFormValidation;
  submitButtonPressed: boolean;
};

const userProperties: {
  email: string;
} = {
  email: 'email',
};

const formFieldsLabels = {
  email: 'User',
};

export function InviteCustomerUserDetailsForm({
  data: formData,
  onChangeData: setFormData,
  validate,
  submitButtonPressed,
}: InviteUsersFormProps) {
  const onInputChange = useCallback(
    (event: InviteCustomerUserFormChangeEvent) => {
      const newValue = event.target.value;
      const name = event.target.name as keyof InviteCustomerUserFields;
      setFormData({
        ...formData,
        [name]: {
          ...formData[name],
          value: newValue,
          ...(submitButtonPressed && { error: null }),
          touched: true,
        },
      });
    },
    [formData, setFormData, submitButtonPressed]
  );

  const onBlur = useCallback(
    (event: InviteCustomerUserFormBlurEvent) => {
      const value = event.target.value;
      const name = event.target.name as keyof InviteCustomerUserFields;
      const error = validate[name]?.(value) ?? null;
      setFormData({
        ...formData,
        [name]: {
          ...formData[name],
          error,
        },
      });
    },
    [formData, setFormData, validate]
  );

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>): void => {
      if (event.key === 'Enter') {
        event.preventDefault();
      }
    },
    []
  );

  return (
    <form>
      <Columns>
        <Columns.Column size="one-fifth">
          <Form.Label
            htmlFor="email"
            className="is-size-7 has-text-weight-normal"
          >
            {formFieldsLabels.email}
          </Form.Label>
          <Form.Field id="email">
            <Form.Control>
              <Form.Input
                type="email"
                placeholder="mail@mail.com"
                className="is-size-7 placeholder-text-gray"
                name={userProperties.email}
                value={formData.email.value}
                onChange={onInputChange}
                onBlur={onBlur}
                onKeyDown={onKeyDown}
              />
            </Form.Control>
            <FeedbackMessage textClassName="is-size-7">
              {formData.email.error}
            </FeedbackMessage>
          </Form.Field>
        </Columns.Column>
      </Columns>
    </form>
  );
}

export default InviteCustomerUserDetailsForm;
